/**
 * queryKeys.ts - Section 49.3's "centralized query keys". Every
 * `useQuery`/`useMutation` call imports its key from here instead of
 * writing an inline array - keeps cache invalidation (`invalidateQueries`)
 * consistent across features instead of relying on everyone remembering
 * the exact same string/array shape.
 */
export const queryKeys = {
  currentUser: ['currentUser'] as const,
  locations: (query: string) => ['locations', query] as const,
  routeSearch: (fromId: string, toId: string) => ['routeSearch', fromId, toId] as const,
  tripSearch: (params: unknown) => ['tripSearch', params] as const,
  trip: (tripPublicId: string) => ['trip', tripPublicId] as const,
  tripAvailability: (tripPublicId: string) => ['tripAvailability', tripPublicId] as const,
  tripPickupPoints: (tripPublicId: string) => ['trip', tripPublicId, 'pickup-points'] as const,
  quote: (
    tripPublicId: string,
    passengerCount: number,
    promoCode: string | null,
    pickupPointPublicId: string | null = null,
    dropOffPointPublicId: string | null = null,
  ) => ['quote', tripPublicId, passengerCount, promoCode, pickupPointPublicId, dropOffPointPublicId] as const,
  myBookings: ['myBookings'] as const,
  booking: (bookingPublicId: string) => ['booking', bookingPublicId] as const,
  bookingTickets: (bookingPublicId: string) => ['bookingTickets', bookingPublicId] as const,
  bookingGroup: (reference: string) => ['bookingGroup', reference] as const,

  // ---------------- Accommodation (Phase 3) ----------------
  publicProperties: ['publicProperties'] as const,
  publicProperty: (propertyPublicId: string) => ['publicProperty', propertyPublicId] as const,
  publicRoomTypes: (propertyPublicId: string) => ['publicRoomTypes', propertyPublicId] as const,
  publicPropertyMedia: (propertyPublicId: string) => ['publicPropertyMedia', propertyPublicId] as const,
  publicPropertyThumbnails: (idsKey: string) => ['publicPropertyThumbnails', idsKey] as const,
  roomAvailability: (propertyPublicId: string, checkIn: string, checkOut: string) =>
    ['roomAvailability', propertyPublicId, checkIn, checkOut] as const,
  myReservations: ['myReservations'] as const,
  reservation: (reservationPublicId: string) => ['reservation', reservationPublicId] as const,

  // ---------------- Property portal / MIS (Phase 4) ----------------
  myProperties: ['myProperties'] as const,
  propertyDashboard: (propertyId: string) => ['propertyDashboard', propertyId] as const,
  propertyStaff: (propertyId: string) => ['propertyStaff', propertyId] as const,
  propertyRoomTypes: (propertyId: string) => ['propertyRoomTypes', propertyId] as const,
  frontDeskBoard: (propertyId: string, date: string) => ['frontDeskBoard', propertyId, date] as const,
  frontDeskReservations: (propertyId: string, from: string, to: string) =>
    ['frontDeskReservations', propertyId, from, to] as const,
  propertyReportSummary: (propertyId: string, from: string, to: string) =>
    ['propertyReportSummary', propertyId, from, to] as const,
  frontDeskReservation: (propertyId: string, reservationId: string) =>
    ['frontDeskReservation', propertyId, reservationId] as const,
  assignableRooms: (propertyId: string, reservationId: string) =>
    ['assignableRooms', propertyId, reservationId] as const,
  folio: (propertyId: string, reservationId: string) => ['folio', propertyId, reservationId] as const,
  housekeepingBoard: (propertyId: string, status: string, floor: string) =>
    ['housekeepingBoard', propertyId, status, floor] as const,

  // ---------------- Property operations (Phase 5) ----------------
  propertySuppliers: (propertyId: string) => ['propertySuppliers', propertyId] as const,
  propertyStores: (propertyId: string) => ['propertyStores', propertyId] as const,
  propertyStock: (propertyId: string) => ['propertyStock', propertyId] as const,
  propertyStockMovements: (propertyId: string, itemId: string) =>
    ['propertyStockMovements', propertyId, itemId] as const,
  propertyPurchaseRequests: (propertyId: string) => ['propertyPurchaseRequests', propertyId] as const,
  propertyPurchaseOrders: (propertyId: string) => ['propertyPurchaseOrders', propertyId] as const,
  propertyReceipts: (propertyId: string) => ['propertyReceipts', propertyId] as const,
  propertyWorkOrders: (propertyId: string, status: string) => ['propertyWorkOrders', propertyId, status] as const,

  // ---------------- Property people & finance (Phase 6) ----------------
  propertyExpenseCategories: (propertyId: string) => ['propertyExpenseCategories', propertyId] as const,
  propertyExpenses: (propertyId: string, status: string) => ['propertyExpenses', propertyId, status] as const,
  propertyExpenseAttachments: (propertyId: string, expenseId: string) =>
    ['propertyExpenseAttachments', propertyId, expenseId] as const,
  propertyFinanceSummary: (propertyId: string, from: string, to: string) =>
    ['propertyFinanceSummary', propertyId, from, to] as const,
  propertyEmployees: (propertyId: string, status: string) => ['propertyEmployees', propertyId, status] as const,
  propertyLeaveRequests: (propertyId: string, status: string) => ['propertyLeaveRequests', propertyId, status] as const,
  propertyAttendance: (propertyId: string, employeeId: string, from: string, to: string) =>
    ['propertyAttendance', propertyId, employeeId, from, to] as const,
  propertyPayPeriods: (propertyId: string) => ['propertyPayPeriods', propertyId] as const,
  propertyPayPeriod: (propertyId: string, periodId: string) => ['propertyPayPeriod', propertyId, periodId] as const,

  // ---------------- Admin hospitality oversight (Phase 7) ----------------
  hospitalityOverview: ['hospitalityOverview'] as const,
  hospitalityScorecard: (propertyId: string) => ['hospitalityScorecard', propertyId] as const,

  // ---------------- Property room setup ----------------
  propertyManagedRoomTypes: (propertyId: string) => ['propertyManagedRoomTypes', propertyId] as const,
  propertyManagedRoomUnits: (propertyId: string) => ['propertyManagedRoomUnits', propertyId] as const,
  propertyRoomSummary: (propertyId: string) => ['propertyRoomSummary', propertyId] as const,
  propertyRoomRates: (propertyId: string, roomTypeId: string) =>
    ['propertyRoomRates', propertyId, roomTypeId] as const,
  propertyMedia: (propertyId: string, roomTypeId: string) =>
    ['propertyMedia', propertyId, roomTypeId] as const,
  propertyActivity: (propertyId: string, entityType: string, page: number) =>
    ['propertyActivity', propertyId, entityType, page] as const,

  // ---------------- Property integrations (Phase 9) ----------------
  propertyWebhooks: (propertyId: string) => ['propertyWebhooks', propertyId] as const,
  propertyWebhookDeliveries: (propertyId: string, endpointId: string) =>
    ['propertyWebhookDeliveries', propertyId, endpointId] as const,
  propertyApiKeys: (propertyId: string) => ['propertyApiKeys', propertyId] as const,

  // ---------------- Admin monetization (Phase 8) ----------------
  hospitalityCommissions: (status: string) => ['hospitalityCommissions', status] as const,
  hospitalityCommissionSummary: ['hospitalityCommissionSummary'] as const,
  subscriptionPlans: ['subscriptionPlans'] as const,
  subscriptions: ['subscriptions'] as const,
  subscriptionSummary: ['subscriptionSummary'] as const,
  subscriptionInvoices: (propertyId: string, status: string) =>
    ['subscriptionInvoices', propertyId, status] as const,
  payment: (paymentPublicId: string) => ['payment', paymentPublicId] as const,
  notifications: ['notifications'] as const,
  wallet: ['wallet'] as const,
  walletTransactions: ['walletTransactions'] as const,
};
